/**
 * InstalarAppAviso — invita a instalar la app en el celular (round 2026-10-29).
 *
 * Pedido del dueño: «si alguien ingresa desde el celular, que salga un anuncio que diga que lo puedes instalar como app,
 * que se lo sugiera si no la detecta, y que funcione en Android y iPhone».
 *
 * Es una hoja inferior NO modal. Cuándo aparece, por qué y qué recuerda: todo está en el hook `usePwaInstalar` (las reglas de
 * visibilidad) y en `utils/plataformaInstalacion.ts` (qué navegador es). Aquí solo se dibuja lo que el hook decide:
 *
 *   - Android con el evento de instalación de Chrome  → texto + botón «Instalar» (abre el diálogo del sistema).
 *   - iPhone/iPad en Safari                            → los pasos Compartir → «Agregar a pantalla de inicio».
 *   - iPhone/iPad en Chrome/Edge/Firefox               → los mismos pasos (+ «ábrela en Safari» si el iOS es antiguo).
 *   - Navegador embebido de otra app (Instagram…)      → pedir que la abra en su navegador (no puede instalar).
 *
 * Se monta una sola vez en `AppRoutes` (dentro del Router y del AuthProvider). Se apaga en /login, en la recuperación de
 * contraseña (para no tapar el formulario ni el widget de Turnstile) y en el visor de documentos (pantalla completa).
 * No cambia ninguna otra pantalla: mientras está abierta solo sube el contenido lo que ella mide, para no tapar el final de la
 * página (ver InstalarAppAviso.css).
 *
 * Accesibilidad: `role="dialog"` con `aria-modal="false"` (no es modal: el resto de la página sigue activa), título y cuerpo
 * enlazados con `aria-labelledby` / `aria-describedby`, X con nombre propio y Esc para cerrar. NO roba el foco al aparecer:
 * la persona estaba haciendo otra cosa.
 */
import React, { useEffect, useId, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { Button } from "antd";
import { CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { usePwaInstalar, type ModoAvisoPwa } from "../hooks/usePwaInstalar";
import "./InstalarAppAviso.css";

/**
 * Rutas donde la hoja no debe salir nunca: pantallas públicas y el visor de documentos (pantalla completa).
 *
 * Se compara en minúsculas y con el `%xx` decodificado porque React Router enruta SIN distinguir mayúsculas (nadie declara
 * `caseSensitive`) y también resuelve `/%6Cogin`: con sesión iniciada, `/LOGIN`, `/Recuperar-Contrasena` o `/VISOR/…`
 * muestran el formulario o el visor igual que la ruta en minúsculas, y la hoja tapaba el formulario (y el widget de Turnstile).
 * `decodeURI` y no `decodeURIComponent`: `%2F` no es una barra de ruta para el router, así que no debe volverse una.
 */
export function rutaPermiteAviso(pathname: string): boolean {
  let ruta = pathname;
  try {
    ruta = decodeURI(pathname);
  } catch {
    // Un `%` mal formado (`/login%`): el router tampoco lo decodifica, se compara tal cual.
  }
  ruta = ruta.toLowerCase();
  return !(
    ruta === "/login" ||
    ruta.startsWith("/login/") ||
    ruta.startsWith("/recuperar-contrasena") ||
    ruta.startsWith("/visor/")
  );
}

export const TITULO_AVISO_PWA = "Instala Aloja Fácil en tu celular";
export const TEXTO_AVISO_PWA =
  "Ábrela con un toque desde tu pantalla de inicio, a pantalla completa y sin buscarla en el navegador.";

/** El ícono «Compartir» de iOS (un cuadro con una flecha hacia arriba): AntD no tiene uno igual. */
const IconoCompartir: React.FC = () => (
  <svg
    className="af-instalar-aviso__icono-compartir"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 15V3" />
    <path d="M8 7l4-4 4 4" />
    <path d="M6 11H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-1" />
  </svg>
);

const Pasos: React.FC<{ modo: ModoAvisoPwa }> = ({ modo }) => (
  <ol className="af-instalar-aviso__pasos">
    <li>
      {modo === "ios-safari" ? (
        <>
          Toca Compartir <IconoCompartir />
          <span className="af-instalar-aviso__ayuda"> (si no lo ves, está en el menú «···»)</span>
        </>
      ) : (
        <>
          Toca Compartir <IconoCompartir />
          <span className="af-instalar-aviso__ayuda"> (en la barra o en el menú «···» del navegador)</span>
        </>
      )}
    </li>
    <li>Elige «Agregar a pantalla de inicio».</li>
    <li>Toca «Agregar».</li>
  </ol>
);

const InstalarAppAviso: React.FC = () => {
  const { token, user } = useAuth();
  const { pathname } = useLocation();
  const habilitado = Boolean(token && user) && rutaPermiteAviso(pathname);
  const pwa = usePwaInstalar({ habilitado });

  const idTitulo = useId();
  const idTexto = useId();
  const raiz = useRef<HTMLDivElement | null>(null);

  // Esc cierra. La hoja no toma el foco, así que el teclado normalmente está en <body>: solo se atiende Esc cuando nadie más
  // lo está usando (foco en <body> o dentro de la hoja). Con un modal, un menú o un campo abierto, Esc es de ellos.
  const { visible, ahoraNo } = pwa;
  useEffect(() => {
    if (!visible) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const objetivo = e.target as Node | null;
      const dentro = Boolean(raiz.current && objetivo && raiz.current.contains(objetivo));
      const sinFoco = objetivo === document.body || objetivo === document.documentElement || objetivo === document;
      if (dentro || sinFoco) ahoraNo();
    };
    document.addEventListener("keydown", alTeclear);
    return () => document.removeEventListener("keydown", alTeclear);
  }, [visible, ahoraNo]);

  // Publica lo que mide la hoja para que el contenido de la página y los botones flotantes suban (InstalarAppAviso.css).
  useLayoutEffect(() => {
    if (!visible) return;
    const el = raiz.current;
    if (!el) return;
    const raizDoc = document.documentElement;
    const medir = () =>
      raizDoc.style.setProperty("--af-instalar-aviso-alto", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    medir();
    document.body.classList.add("af-instalar-aviso-abierto");
    const observador = typeof ResizeObserver !== "undefined" ? new ResizeObserver(medir) : null;
    observador?.observe(el);
    window.addEventListener("resize", medir);
    return () => {
      observador?.disconnect();
      window.removeEventListener("resize", medir);
      document.body.classList.remove("af-instalar-aviso-abierto");
      raizDoc.style.removeProperty("--af-instalar-aviso-alto");
    };
  }, [visible, pwa.modo]);

  if (!pwa.visible || !pwa.modo) return null;

  const { modo } = pwa;
  const esIos = modo !== "android";
  const textoEmbebido = pwa.appEmbebida
    ? `Estás dentro de ${pwa.appEmbebida}. Ábrela en tu navegador (Safari o Chrome) para poder instalarla.`
    : "Ábrela en tu navegador (Safari o Chrome) para poder instalarla.";

  return createPortal(
    <div className="af-instalar-aviso" ref={raiz} data-testid="instalar-app-aviso" data-modo={modo}>
      <div
        className="af-instalar-aviso__tarjeta"
        role="dialog"
        aria-modal="false"
        aria-labelledby={idTitulo}
        aria-describedby={idTexto}
      >
        <div className="af-instalar-aviso__cabecera">
          {/* Sin `?v=`: serve.json entrega los PNG con no-cache, así que siempre es el logo vigente (el `?v=` es para los íconos
              de la app instalada, que el navegador cachea por su cuenta). */}
          <img
            className="af-instalar-aviso__logo"
            src={`${process.env.PUBLIC_URL ?? ""}/logo-mark-192.png`}
            alt=""
            width={44}
            height={44}
          />
          <h2 id={idTitulo} className="af-instalar-aviso__titulo">
            {TITULO_AVISO_PWA}
          </h2>
          <button
            type="button"
            className="af-instalar-aviso__cerrar"
            aria-label="Cerrar aviso de instalación"
            onClick={pwa.ahoraNo}
          >
            <CloseOutlined aria-hidden="true" />
          </button>
        </div>

        <p id={idTexto} className="af-instalar-aviso__texto">
          {modo === "ios-embebido" ? textoEmbebido : TEXTO_AVISO_PWA}
        </p>

        {(modo === "ios-safari" || modo === "ios-otro") && <Pasos modo={modo} />}
        {pwa.notaSafari && <p className="af-instalar-aviso__nota">Si no ves la opción, ábrela en Safari.</p>}

        <div className="af-instalar-aviso__acciones">
          {modo === "android" && (
            <Button type="primary" icon={<DownloadOutlined aria-hidden="true" />} loading={pwa.instalando} onClick={pwa.instalar}>
              Instalar
            </Button>
          )}
          {esIos && modo !== "ios-embebido" && <Button onClick={pwa.yaLaInstale}>Ya la instalé</Button>}
          <Button onClick={pwa.ahoraNo} disabled={pwa.instalando}>
            Ahora no
          </Button>
          <Button
            type="link"
            className="af-instalar-aviso__no-mostrar"
            onClick={pwa.noVolverAMostrar}
            disabled={pwa.instalando}
          >
            No volver a mostrar
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default InstalarAppAviso;
